import dayjs from 'dayjs';
import { anyRowRate } from './taxCalculation';

/*
 * Reapproval check for editing an Authorized work order — see docs/work-orders/WORK_ORDERS_SPEC.md §4.4.
 *
 * Only a change the customer would need to re-authorize counts: customer, vehicle, work order date,
 * any priced row (fixed price, labour qty/rate, part qty/price, adding or removing one), fees,
 * discount, taxes (applied / not applied and the rate — document summary or a line) and the
 * authorization wording. Service advisor, customer PO, memo, descriptions, mileage,
 * notes, recommendations and rows without pricing never do.
 *
 * Both sides are reduced to the same plain signature — the saved record (API response) and the
 * outgoing update payload — so the comparison does not depend on editor state hydration.
 */

// Editor values can arrive formatted ("1,200.00"), so strip separators before comparing.
const money = (value) => (Number(String(value ?? '').replace(/,/g, '')) || 0).toFixed(2);
const idOf = (value) => (value && typeof value === 'object' ? value._id : value) || '';
const text = (value) => (value || '').trim();

/**
 * Which taxes are applied and at what rate, as a sorted `taxId:rate` list. The saved record's summary
 * taxes carry the rate as `taxPercentage` (no rate key), which is also what the editor loads from — so
 * read it the same way, or an untouched record compares as 0% vs the rate on screen.
 * — the "T" vs "N/T" toggle on
 * a line and the checkboxes in the bottom summary, plus the rate typed into the summary (10% → 12%).
 * Empty when taxes are not compared (legacy tax version).
 */
const appliedTaxes = (breakdown, withTaxes) =>
    withTaxes
        ? (breakdown || [])
              .filter((tax) => tax?.applyTax)
              .map((tax) => `${idOf(tax?.taxId)}:${money(tax?.workOrderAuthorizedRate ?? anyRowRate(tax) ?? tax?.taxPercentage ?? tax?.taxRate ?? tax?.rate)}`)
              .sort()
              .join(',')
        : '';

/**
 * Priced rows only, in order. A service with no pricing contributes nothing.
 * Part price: the payload sends the line price as `retailPrice`; the saved record keeps it as `cost`
 * (its `retailPrice` can be the inventory's own retail price), so read `cost` first for the record.
 */
const pricingRows = (services = [], withTaxes, isRecord = false) =>
    services.flatMap((service) => {
        const rows = [];
        if (Number(service?.fixedPrice) > 0) {
            rows.push(`fixed|${idOf(service?.serviceId)}|${money(service.fixedPrice)}|${appliedTaxes(service.taxesBreakdown, withTaxes)}`);
        }
        if (Number(service?.labor?.qty) > 0 && Number(service?.labor?.ratePerHour) > 0) {
            rows.push(
                `labour|${idOf(service?.serviceId)}|${money(service.labor.qty)}|${money(service.labor.ratePerHour)}|${appliedTaxes(service.labor.taxesBreakdown, withTaxes)}`
            );
        }
        (service?.parts || []).forEach((part) => {
            const partKey = idOf(part?.inventoryId) || idOf(part?.inventory) || text(part?.partName);
            const linePrice = isRecord ? (part?.cost ?? part?.retailPrice) : part?.retailPrice;
            rows.push(`part|${partKey}|${money(part?.totalQty)}|${money(linePrice)}|${appliedTaxes(part?.taxesBreakdown, withTaxes)}`);
        });
        return rows;
    });

const feeRows = (fees = [], withTaxes) =>
    fees.map((fee) => {
        const isFixed = fee?.feesCalculationType === 'fixed_amount';
        const value = isFixed ? money(fee?.feesAmount) : money(fee?.feesPercentage);
        return `${idOf(fee?.feesId) || text(fee?.feesName)}|${fee?.feesCalculationType}|${value}|${appliedTaxes(fee?.taxesBreakdown, withTaxes)}`;
    });

const discountKey = (source) => `${money(source?.discount)}|${money(source?.discountPercentage)}|${source?.discountSource || ''}`;

/**
 * Which reapproval fields differ between the saved record and the outgoing payload.
 *
 * @param {object} record   work order detail response (the saved state)
 * @param {object} payload  outgoing update payload
 * @param {object} context  { workOrderDay: 'YYYY-MM-DD' | '', timezone, customerIds: ids the editor knows the
 *                           current customer by (_id and refIdUsers — the payload sends refIdUsers first) }
 * @returns {string[]} names of the changed fields — empty when nothing needs reapproval
 */
export const getReapprovalChanges = (record, payload, context) => getReapprovalDiff(record, payload, context).map((entry) => entry.field);

/**
 * Same check, but each entry also carries the saved and the outgoing value — logged by the editor so an
 * unexpected Reauthorization Required popup can be traced to the exact field.
 */
export const getReapprovalDiff = (record, payload, { workOrderDay = '', timezone, customerIds = [] } = {}) => {
    if (!record) return [];
    const diff = [];
    const compare = (field, saved, current) => {
        if (saved !== current) diff.push({ field, saved, current });
    };

    const customer = record.customer || {};
    const savedCustomerIds = [idOf(customer), idOf(customer.refIdUsers), idOf(record.customerId)].filter(Boolean);
    const currentCustomerIds = [idOf(payload.customerId), ...customerIds.map(idOf)].filter(Boolean);
    if (currentCustomerIds.length && !currentCustomerIds.some((customerId) => savedCustomerIds.includes(customerId))) {
        diff.push({ field: 'customer', saved: savedCustomerIds.join(','), current: currentCustomerIds.join(',') });
    }

    compare('vehicle', idOf(record.vehicle) || idOf(record.vehicleId), idOf(payload.vehicleId));
    compare('workOrderDate', record.workOrderDate ? dayjs(record.workOrderDate).tz(timezone).format('YYYY-MM-DD') : '', workOrderDay);
    compare('authorizationMessage', text(record.authorizationMessage), text(payload.authorizationMessage));
    compare('discount', discountKey(record), discountKey(payload));

    // Taxes are compared only when the editor sends them (new tax version); legacy records send none.
    const withTaxes = Array.isArray(payload.taxesBreakdown);
    compare('taxes', appliedTaxes(record.taxesBreakdown, withTaxes), appliedTaxes(payload.taxesBreakdown, withTaxes));
    compare('fees', JSON.stringify(feeRows(record.fees, withTaxes)), JSON.stringify(feeRows(payload.fees, withTaxes)));
    compare('pricedRows', JSON.stringify(pricingRows(record.services, withTaxes, true)), JSON.stringify(pricingRows(payload.services, withTaxes)));

    return diff;
};

/** True when any reapproval field changed (see getReapprovalChanges). */
export const hasReapprovalChanges = (record, payload, context) => getReapprovalChanges(record, payload, context).length > 0;
